import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  Box,
  Grid,
  Tabs,
  Tab,
  useTheme,
  Typography,
  useMediaQuery,
  TextField,
} from "@mui/material";
import {
  BackspaceOutlined,
} from "@mui/icons-material";
import Button from "../Button";
import "./SignaturePad.css";
import DropdownField from "../DropdownField";
import { Control, Controller } from "react-hook-form";
import CustomTextField from "../CustomTextField";
import { useModalStyles } from "../useModalStyles";

const availableFonts = [
  {
    value: "Dancing Script",
    label: "Dancing Script",
    style: { fontFamily: "Dancing Script" },
  },
  // {
  //   value: "Courgette",
  //   label: "Courgette",
  //   style: { fontFamily: "Courgette" },
  // },
  // {
  //   value: "Indie Flower",
  //   label: "Indie Flower",
  //   style: { fontFamily: "Indie Flower" },
  // },
  // {
  //   value: "Shadows Into Light",
  //   label: "Shadows Into Light",
  //   style: { fontFamily: "Shadows Into Light" },
  // },
  // {
  //   value: "Permanent Marker",
  //   label: "Permanent Marker",
  //   style: { fontFamily: "Permanent Marker" },
  // },
  // {
  //   value: "Homemade Apple",
  //   label: "Homemade Apple",
  //   style: { fontFamily: "Homemade Apple" },
  // },
];

export interface SignatureData {
  signature: string;
  typedText: string;
}

interface SignatureProps {
  onSignatureChange: (data: SignatureData) => void;
  isError?: boolean;
  name: string;
  textName?: string;
  control: Control<any, any>;
  validationRules?: {
    name?: Object;
    signature?: Object;
    title?: Object;
  };
  setSignOutside?: string;
  signature?: string;
  signerName?: string;
  disabled?: boolean;
  enableTitle?: boolean;
}

const SignaturePad: React.FC<SignatureProps> = ({
  onSignatureChange,
  setSignOutside,
  isError,
  name,
  textName,
  control,
  validationRules = {},
  signature,
  signerName,
  disabled = false,
  enableTitle = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { tokens: mTokens, isDark, inputFieldSx, dropdownWrapSx, cancelBtnSx } = useModalStyles();

  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.7, display: "flex", alignItems: "center", gap: 0.4 }}>
      {label}{required && <Box component="span" sx={{ color: mTokens.accent }}>*</Box>}
    </Typography>
  );

  const controlHeight = "42px";
  const controlHeightSx = {
    minHeight: `${controlHeight} !important`,
    height: `${controlHeight} !important`,
    maxHeight: `${controlHeight} !important`,
    boxSizing: "border-box !important",
    alignItems: "center",
  };
  const mergeControlSx = (base: object) => {
    const nested = (base as Record<string, unknown>)["& .MuiOutlinedInput-root, & .MuiInputBase-root"];
    return {
      ...(nested && typeof nested === "object" ? nested : {}),
      ...controlHeightSx,
    };
  };
  const textFieldWrapSx = (hasError = false) => {
    const base = inputFieldSx(hasError);
    return {
      ...base,
      width: "100%",
      "& .MuiFormControl-root, & .MuiTextField-root": { width: "100%" },
      "& .MuiOutlinedInput-root:not(.MuiInputBase-multiline), & .MuiInputBase-root:not(.MuiInputBase-multiline)": mergeControlSx(base),
    };
  };
  const dropdownFieldWrapSx = (hasError = false) => {
    const base = dropdownWrapSx(hasError);
    return {
      ...base,
      width: "100%",
      "& .MuiAutocomplete-root, & .MuiFormControl-root": { width: "100%", maxWidth: "100%" },
      "& .MuiOutlinedInput-root, & .MuiInputBase-root": mergeControlSx(base),
    };
  };
  const tabBarSx = {
    minHeight: 40,
    p: 0.5,
    mb: 1.5,
    borderRadius: "999px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.025)",
    width: "fit-content",
    maxWidth: "100%",
    "& .MuiTabs-indicator": { display: "none" },
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 700,
      fontSize: "0.86rem",
      minHeight: 32,
      minWidth: { xs: 120, sm: 140 },
      borderRadius: "999px",
      color: mTokens.textSecondary,
      px: 2,
      "&.Mui-selected": {
        color: "#fff",
        background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
        boxShadow: "0 2px 8px rgba(13,148,136,0.35)",
      },
    },
  };
  const clearBtnSx = {
    ...cancelBtnSx,
    py: 0.9,
    fontSize: "0.86rem",
    "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
    "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
  };

  const signatureRef = useRef<SignatureCanvas>(null);
  const [typedSignature, setTypedSignature] = useState("");
  const [fontSize, setFontSize] = useState(isMobile ? 40 : 60);
  const [fontStyle, setFontStyle] = useState("Dancing Script");
  const [view, setView] = useState<"draw" | "type" | null>("draw");
  const [isSignatureProvided, setIsSignatureProvided] = useState(false);
  const [isChangeDropdown, setIsChangeDropdown] = useState(false);

  useEffect(() => {
    document.fonts.load("1em 'Dancing Script'").then(() => {
      setFontStyle("Dancing Script");
      if (view === "type") {
        drawTypedSignature();
      }
    });
  }, [view]);
  

  const handleTypedSignatureChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newText = e.target.value;
    setIsChangeDropdown(false);
    setTypedSignature(newText);
    setSignature(newText);
  };

  const calculateFontSize = (text: string, canvasWidth: number) => {
    const maxFontSize = isMobile ? 40 : 60;
    const minFontSize = 10;
    const context = document.createElement("canvas").getContext("2d");
    if (context) {
      context.font = `${maxFontSize}px '${fontStyle}'`;
      let fontSize = maxFontSize;
      while (context.measureText(text).width > canvasWidth && fontSize > minFontSize) {
        fontSize -= 1;
        context.font = `${fontSize}px '${fontStyle}'`;
      }
      return fontSize;
    }
    return maxFontSize;
  };

  const setSignature = (setText: string) => {
    if (view === "type" && signatureRef.current) {
      const canvas = signatureRef.current.getCanvas();
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const calculatedFontSize = calculateFontSize(setText, canvas.width);
        console.log(ctx.canvas.height);
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        ctx.font = isChangeDropdown ? `${fontSize}px '${fontStyle}'` : `${calculatedFontSize}px '${fontStyle}'`;
        ctx.fillStyle = "black";

        const text = setText;
        // const textWidth = ctx.measureText(text).width;
        const textHeight = isChangeDropdown ? fontSize : calculatedFontSize;

        // Calculate the center position
        // const x = (canvas.width - textWidth) / 2;
        // const y = (canvas.height + textHeight) / 2;
        //CS-299 : KS : Changes Signature Field in Product Agreements
        const x = 0; // Starting from the left edge of the canvas
        const y = (canvas.height + textHeight) / 2;
        //
        ctx.fillText(text, x, y);

        const base64Data = canvas.toDataURL("image/png");
        const base64Substringed = base64Data.substring(
          "data:image/png;base64,".length
        );
        onSignatureChange({
          signature: base64Substringed,
          typedText: setText,
        });
        setIsSignatureProvided(true);
      }
    }
  };

  useEffect(() => {
    if (signature && signatureRef.current) {
      const image = new Image();
      image.onload = () => {
        const canvas = signatureRef.current?.getCanvas();
        const ctx = canvas?.getContext("2d");
        if (ctx) {
          signatureRef.current?.clear();
          ctx.drawImage(image, 0, 0);

          ctx.globalCompositeOperation = "source-in";
          ctx.fillStyle = theme.palette.mode === "dark" ? "white" : "black";
          ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
          ctx.globalCompositeOperation = "source-over";
        }
      };
      image.src = `data:image/png;base64,${signature}`;
    }
  }, [signature]);

  // CS-1461 - Start
  useEffect(() => {
    if (view === "draw") {
      const nameToUse = signerName || setSignOutside || "";
      setTypedSignature(nameToUse);
      onSignatureChange({
        signature: "",
        typedText: nameToUse,
      });
    }
  }, [view]);
  // CS-1461 - End

  const drawTypedSignature = () => {
    if (signatureRef.current) {
      const canvas = signatureRef.current.getCanvas();
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const calculatedFontSize = calculateFontSize(typedSignature, canvas.width);
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        ctx.font = isChangeDropdown ? `${fontSize}px '${fontStyle}'` : `${calculatedFontSize}px '${fontStyle}'`;
        ctx.fillStyle = theme.palette.mode === "dark" ? "white" : "black";
        const text = typedSignature;
        const textWidth = ctx.measureText(text).width;
        const textHeight = isChangeDropdown ? fontSize : calculatedFontSize;

        // Calculate the center position
        const x = (canvas.width - textWidth) / 2;
        const y = (canvas.height + textHeight) / 2;
        ctx.fillText(text, x, y);
      }
    }
  };

  const getPenColor = () => {
    if (theme.palette.mode === "dark") {
      return '#FFFFFF'
    } else {
      return 'black'
    }
  }

  useEffect(() => {
    if (setSignOutside) {
      setView("type");
      setTypedSignature(setSignOutside);
      setSignature(setSignOutside);
    } else {
      clearSignature();
    }
  }, [setSignOutside]);

  useEffect(() => {
    if (setSignOutside) {
      if (view === "type") {
        setSignature(setSignOutside);
      }
      setTypedSignature(setSignOutside);
    }
  }, [view]);

  useEffect(() => {
    if (view === "type") {
      drawTypedSignature();
    }
  }, [fontSize, fontStyle, typedSignature, view, theme]);

  const handleFontSizeChange = (e: any) => {
    setIsChangeDropdown(true);
    setFontSize(Number(e.target.value));
    if (view === "type") {
      drawTypedSignature();
    }
  };

  const handleFontStyleChange = (e: any) => {
    setFontStyle(e.target.value as string);
    if (view === "type") {
      drawTypedSignature();
    }
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
    onSignatureChange({
      signature: "",
      typedText: "",
    });
    setTypedSignature("");
    setIsSignatureProvided(false);
  };

  const loadImage = (src: any) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = (error) => reject(error);
      image.src = src;
    });
  };

  const saveSignature = async () => {
    const signatureSrc = signatureRef.current?.toDataURL("image/png");
    if (!signatureSrc) return;
    const signatureImage = await loadImage(signatureSrc);
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    //@ts-ignore
    canvas.width = signatureImage?.width;
    //@ts-ignore
    canvas.height = signatureImage?.height;
    //@ts-ignore
    context?.drawImage(signatureImage, 0, 0);
    const imageData = context?.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData?.data;
    if (data) {
      for (let i = 0; i < data.length; i += 4) {
        data[i] = 0;
        data[i + 1] = 0;
        data[i + 2] = 0;
      }
      context?.putImageData(imageData, 0, 0);
      const base64Data = canvas.toDataURL("image/png");
      const base64Substringed = base64Data?.substring(
        "data:image/png;base64,".length
      );
      if (base64Substringed) {
        onSignatureChange({
          signature: base64Substringed,
          typedText: typedSignature,
        });
        setIsSignatureProvided(true);
      }
    }
  };

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: "draw" | "type"
  ) => {
    setView(newValue);
    clearSignature();
  };

  useEffect(() => {
    const updatePenColor = async () => {
      const newPenColor = getPenColor();

      if (signatureRef.current && !signatureRef.current.isEmpty()) {
        const canvas = signatureRef.current.getCanvas();
        const dataURL = signatureRef.current.toDataURL();
        const newSignatureData = await changeSignatureColor(dataURL, newPenColor, canvas.width, canvas.height);
        signatureRef.current.clear();
        signatureRef.current.fromDataURL(newSignatureData);
      }
    };

    updatePenColor();
  }, [theme.palette.mode]);

  const changeSignatureColor = (signatureData: string, newColor: string, canvasWidth: number, canvasHeight: number): Promise<string> => {

    return new Promise((resolve) => {
      const image = new Image();
      image.src = signatureData;

      const offscreenCanvas = document.createElement('canvas');
      offscreenCanvas.width = canvasWidth;
      offscreenCanvas.height = canvasHeight;
      const offscreenContext = offscreenCanvas.getContext('2d');

      image.onload = () => {
        // Draw the image on the offscreen canvas
        if (offscreenContext) {
          offscreenContext.drawImage(image, 0, 0);

          // Get image data and replace color
          const imageData = offscreenContext.getImageData(0, 0, offscreenCanvas.width, offscreenCanvas.height);
          const data = imageData.data;

          const r = parseInt(newColor.substring(1, 3), 16); // Red
          const g = parseInt(newColor.substring(3, 5), 16); // Green
          const b = parseInt(newColor.substring(5, 7), 16); // Blue

          for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] > 0) { // Check alpha channel for non-transparent pixels
              data[i] = r; // Red
              data[i + 1] = g; // Green
              data[i + 2] = b; // Blue
            }
          }

          offscreenContext.putImageData(imageData, 0, 0);

          resolve(offscreenCanvas.toDataURL());
        }
      };
    });
  };

  return (
    <Box
      sx={{
        border: `1px solid ${mTokens.border}`,
        borderRadius: "14px",
        padding: { xs: 1.25, sm: 1.5 },
        backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)",
      }}
    >
      <Tabs
        value={view}
        onChange={handleChange}
        sx={tabBarSx}
      >
        <Tab
          label="Draw signature"
          value="draw"
          disabled={disabled}
        />
        <Tab
          label="Type signature"
          value="type"
          disabled={disabled}
        />
      </Tabs>

      <Grid
        container
        spacing={2}
        sx={{
          textAlign: "left",
          alignItems: "flex-start",
          justifyContent: "center",
          mt: "1px",
        }}
      >
        {enableTitle && (
          <Grid item xs={12} sm={3} md={2}>
            <FieldLabel label="Title" required />
            <Box sx={textFieldWrapSx()}>
              <CustomTextField
                controllerName={'title'}
                control={control}
                variant="outlined"
                fullWidth
                label=""
                size="small"
                placeholder="Title"
                rules={validationRules.title || {}}
                disabled={disabled}
              />
            </Box>
          </Grid>
        )}
        <Grid item xs={12} sm={enableTitle ? 9 : 12} md={enableTitle ? 10 : 12}>
          <FieldLabel label="Name" required />
          <Box sx={textFieldWrapSx()}>
            <CustomTextField
              controllerName={textName ? textName : "name"}
              control={control}
              variant="outlined"
              fullWidth
              size="small"
              onChange={handleTypedSignatureChange}
              onBlur={view === "type" ? drawTypedSignature : undefined}
              placeholder="Confirm your name and signature"
              rules={validationRules.name || {}}
              disabled={disabled}
            />
          </Box>
        </Grid>

        <Controller
          name={name}
          control={control}
          rules={validationRules.signature || {}}
          render={({ field, fieldState: { error } }) => (
            <>
              <Grid
                item
                xs={12}
                md={12}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    border: "1px solid",
                    width:isMobile ? "100%" : "500px",
                    borderRadius: "12px",
                    borderColor:
                      error && !isSignatureProvided
                        ? mTokens.error
                        : mTokens.inputBorder,
                    backgroundColor: mTokens.inputBg,
                    marginBottom: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                  style={{
                    pointerEvents: view === "type" ? "none" : "auto",
                  }}
                >
                  <SignatureCanvas
                    penColor={theme.palette.mode === "dark" ? "white" : "black"}
                    canvasProps={{
                    width:  isMobile ? "100%" : 500,
                    height: isMobile ? 180 : 100,
                    }}
                    ref={signatureRef}
                    onEnd={view === "draw" ? saveSignature : undefined}
                  />
                </Box>
                {error && !isSignatureProvided && (
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ marginLeft: 2, marginBottom: 1 }}
                  >
                    {error.message || "Signature is required"}
                  </Typography>
                )}
                <Box
                mt={1}
                 sx={{
                width: isMobile ? "100%" : "500px",
                }}
>
                  <Grid
                    container
                    justifyContent={(view === "type" && typedSignature !=="") ? "space-between" : "flex-end"}
                    spacing={1}
                    alignItems="flex-end"
                  >
                    {(view === "type" && typedSignature !=="") && (
                      <>
                        <Grid item xs={12} md>
                          <FieldLabel label="Font Size" />
                          <Box sx={dropdownFieldWrapSx()}>
                            <DropdownField
                              options={[
                                { value: 20, label: "20px" },
                                { value: 40, label: "40px" },
                                { value: 50, label: "50px" },
                                { value: 60, label: "60px" },
                              ]}
                              label=""
                              disableSearch
                              size="small"
                              onChangeSelect={handleFontSizeChange}
                              value={fontSize}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md="auto" sx={{ minWidth: { md: 200 } }}>
                          <FieldLabel label="Font Style" />
                          <Box
                            sx={{
                              ...textFieldWrapSx(),
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: `${mTokens.textPrimary} !important`,
                                color: `${mTokens.textPrimary} !important`,
                                opacity: 1,
                              },
                            }}
                          >
                            <TextField
                              size="small"
                              variant="outlined"
                              value={fontStyle}
                              disabled
                              fullWidth
                              inputProps={{
                                "aria-label": "Font Style",
                                style: { fontFamily: `'${fontStyle}', cursive` },
                              }}
                            />
                          </Box>
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} md="auto">
                      <Button
                        onClick={clearSignature}
                        color="error"
                        variant="outlined"
                        startIcon={<BackspaceOutlined />}
                        disabled={disabled}
                        fullWidth
                        removeUpperCase
                        sx={clearBtnSx}
                      >
                        Clear
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </>
          )}
        />
      </Grid>
    </Box>
  );
};

export default SignaturePad;

